import type { FastGoalCreationPreview, FastGoalInitialOptimizationReadback } from './types';

export type FastGoalOptimizationPresentation =
  | 'pending'
  | 'stale'
  | 'failed'
  | 'degraded'
  | 'completed';

export function isFastGoalOptimizationRecoverable(
  readback: Pick<FastGoalInitialOptimizationReadback, 'status'> | null | undefined,
): boolean {
  return readback?.status === 'queued'
    || readback?.status === 'running'
    || readback?.status === 'stale';
}

export function classifyFastGoalOptimization(
  readback: FastGoalInitialOptimizationReadback | null | undefined,
): FastGoalOptimizationPresentation | null {
  if (!readback) return null;
  if (readback.status === 'queued' || readback.status === 'running') return 'pending';
  if (readback.status === 'stale') return 'stale';
  if (readback.status === 'failed' || readback.result === 'failed') return 'failed';
  if (
    readback.status === 'degraded'
    || readback.result === 'completed_with_warnings'
    || (readback.warning_codes?.length ?? 0) > 0
  ) return 'degraded';
  return 'completed';
}

/**
 * #1827: screenshot-sourced goal detection, mirroring the server-side
 * `created_from`-substring convention in
 * `src/analyzers/missing-pieces-analyzer.ts` (`phase2RoutingSource`). Not
 * limited to the literal `created_from === "screenshot"` value, since capture
 * clients persist `${clientName}_screenshot` (see `src/routes/analyze.ts`).
 */
export function isScreenshotSourcedGoal(
  goal: { source_type?: string | null; created_from?: string | null } | null | undefined,
): boolean {
  if (!goal) return false;
  if (goal.source_type === 'screenshot') return true;
  return (goal.created_from ?? '').toLowerCase().includes('screenshot');
}

/**
 * #1827: the deterministic first-row builder persists a localized generic
 * title (`画像から作成したゴール` / `Goal created from image`) for
 * screenshot captures with no usable source title. Processing state is never
 * encoded into that persisted title; the UI instead projects a transient
 * label while `fast_goal_initial_optimization` is still queued/running (see
 * "Screenshot provisional title and processing state" in
 * docs/architecture/2026-06-17-fast-goal-creation-contract.md).
 */
export const SCREENSHOT_PROVISIONAL_TITLES = ['画像から作成したゴール', 'Goal created from image'] as const;

/**
 * #2660: once Jev has written the title and conditions, the running
 * generation carries them so the card can show the Goal while the Actions are
 * still being proposed. Only a running generation's preview is shown.
 */
export function fastGoalCreationPreview(
  goal: { fast_goal_initial_optimization?: FastGoalInitialOptimizationReadback | null } | null | undefined,
): FastGoalCreationPreview | null {
  const readback = goal?.fast_goal_initial_optimization;
  if (readback?.status !== 'running') return null;
  const preview = readback.creation_preview;
  if (!preview || typeof preview.title !== 'string' || !preview.title.trim()) return null;
  return {
    title: preview.title.trim(),
    conditions: Array.isArray(preview.conditions)
      ? preview.conditions.filter((condition): condition is string => typeof condition === 'string' && condition.trim().length > 0)
      : [],
  };
}

/** The conditions line under a previewed title; empty when it only repeats the title. */
export function fastGoalCreationPreviewConditionsText(preview: FastGoalCreationPreview): string {
  const text = preview.conditions.join(' / ');
  return text === preview.title ? '' : text;
}

export function isScreenshotFirstRowProcessing(
  goal: {
    title?: string | null;
    source_type?: string | null;
    created_from?: string | null;
    fast_goal_initial_optimization?: FastGoalInitialOptimizationReadback | null;
  } | null | undefined,
): boolean {
  if (!isScreenshotSourcedGoal(goal)) return false;
  // The contract scopes the transient label to the no-usable-source-title
  // case, where the persisted title is the localized provisional value. A
  // goal that already carries a real title (0/1 lightweight/broad mode, or a
  // deterministic capture with a usable source title) must not show it.
  if (!SCREENSHOT_PROVISIONAL_TITLES.includes((goal?.title ?? '').trim() as (typeof SCREENSHOT_PROVISIONAL_TITLES)[number])) {
    return false;
  }
  if (fastGoalCreationPreview(goal)) return false;
  const status = goal?.fast_goal_initial_optimization?.status;
  return status === 'queued' || status === 'running';
}
